import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ExceptionManager } from "Core/logManager"
import { clientQuery } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { updateDriverPerformanceCase } from "../modals/editCoachPanel/redux/slice";

const INIT_STATE = {
    loading: false,
    cases: [],
    total: 0,
    error: null,
    nextToken: null,
    lastSearchRange: null,
};

const route = "modules/driverPerformance/views/cases/redux/slice";

const searchDriverPerformanceCases = createAsyncThunk(
    "driverPerformance/casesTab/driverPerformanceSearchCases",
    async (payload, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            let pagination = payload.conditions.find((item) => item.page);
            const currentNextToken = pagination === 1 ? '' : state.driverPerformanceCasesTabRedux.nextToken;
            const currentConditions = payload.conditions.filter((item) => !item.page);

            // Check if caseNumber is present in the conditions and driver_name is not
            const hasCaseNumber = currentConditions.some(condition =>
                condition.fields &&
                condition.fields.includes('caseNumber') &&
                !condition.fields.includes('driver_name')
            );

            let params = {
                limit: 50,
                nextToken: currentNextToken || '',
                conditions: currentConditions,
                sort: payload?.sort || [{ field: 'createdOn', order: 'DESC' }],
            }

            const response = await clientQuery(
                searchDriverPerformanceCases.query,
                { ...params },
                endpoints.GRAPHQL_GENERAL_V2
            );

            return response?.data ? { ...response?.data, hasCaseNumber } : { items: [], nextToken: null };
        } catch (exc) {
            ExceptionManager(exc, route, "searchDriverPerformanceCases");
            return rejectWithValue(exc);
        }
    }
);

searchDriverPerformanceCases.query = `
    query searchDriverPerformanceCases ($conditions: [DriverPerformanceCaseSearchInput], $sort: [DriverPerformanceCaseSortInput], $limit: Int, $offset: Int, $nextToken: String) {
            data: searchDriverPerformanceCases (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset, nextToken: $nextToken) {
                items {
                    id
                    caseNumber
                    driverId
                    coachId
                    driver {
                        name
                        tagIds
                        tagNames
                    }
                    coach{
                        firstName
                        lastName
                    }
                    status{
                        entityId
                        label
                        nextStep
                        nextStatusId
                    }
                    statusId
                    completedDate
                    driverCaseEventsCount
                    createdOn
                }
                nextToken
                total
            }
        }`;

export { searchDriverPerformanceCases };

const driverPerformanceCasesTabRedux = createSlice({
    name: route,
    initialState: INIT_STATE,
    reducers: {
        reset: (state, action) => {
            return action.payload
                ? { ...state, ...action.payload }
                : { ...INIT_STATE };
        },
        setCasesFilters: (state, action) => {
            return {
                ...state,
                filters: {
                    events: action.payload?.conditions
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(searchDriverPerformanceCases.pending, (state, action) => {
            const { conditions } = action.meta.arg
            let pagination = conditions.find((item) => item.page);

            const toIsoStringOrNull = (value) => {
                if (!value) return null;
                const date = value instanceof Date ? value : new Date(value);
                return Number.isNaN(date.getTime()) ? null : date.toISOString();
            };

            const rangeCondition = conditions?.find((item) => item?.range?.GTE && item?.range?.LTE);
            const gte = toIsoStringOrNull(rangeCondition?.range?.GTE);
            const lte = toIsoStringOrNull(rangeCondition?.range?.LTE);

            state.lastSearchRange = gte && lte ? { GTE: gte, LTE: lte } : null;

            // Reset cases when page is 1 OR when pagination object doesn't exist (new search)
            if (!pagination || pagination?.page === 1) {
                state.cases = []
                state.nextToken = null
            }

            state.loading = true;

        })
        builder.addCase(searchDriverPerformanceCases.fulfilled, (state, action) => {
            const newItems = action.payload?.items || [];
            const nextToken = action.payload?.nextToken || null;
            const itemsWithCountId = newItems.map((item, index) => ({
                ...item,
                countId: state.cases.length + index + 1
            }));

            state.cases = [...state.cases, ...itemsWithCountId]

            state.nextToken = nextToken
            state.loading = false;
        })
        builder.addCase(searchDriverPerformanceCases.rejected, (state, action) => {
            state.cases = [];
            state.loading = false;
            state.error = action.payload;
        })

        // Update specific case's coach locally when editCoachPanel update succeeds
        builder.addCase(updateDriverPerformanceCase.fulfilled, (state, action) => {
            const updated = action.payload;
            if (!updated?.id) return;

            // Support both shapes: cases as an array or as an object with items
            const list = Array.isArray(state.cases)
                ? state.cases
                : (state.cases && Array.isArray(state.cases.items) ? state.cases.items : null);

            if (!list) return;

            const idx = list.findIndex((c) => String(c?.id) === String(updated.id));
            if (idx !== -1) {
                const existing = list[idx] || {};
                const newCoach = {
                    ...(existing.coach || {}),
                    userName: updated.coachUserName || existing.coach?.userName || "",
                    firstName: updated.coachFirstName || existing.coach?.firstName || existing.coach?.name || "",
                    lastName: updated.coachLastName || existing.coach?.lastName || "",
                };
                const updatedCase = {
                    ...existing,
                    coachId: updated.coachId,
                    coach: newCoach,
                    statusId: existing?.statusId == "pendingAssign" ? "pendingInitiate" : existing?.statusId,
                    status: existing?.statusId == "pendingAssign" ? {
                        ...existing?.status,
                        entityId: "pendingInitiate",
                        label: "Pending",
                        nextStep: "Initiate Coaching",
                    } : existing?.status
                };

                if (Array.isArray(state.cases)) {
                    state.cases[idx] = updatedCase;
                } else if (state.cases && Array.isArray(state.cases.items)) {
                    state.cases.items[idx] = updatedCase;
                }
            }
        })

    },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = driverPerformanceCasesTabRedux;

// Extract and export each action creator by name
export const { reset, setCasesFilters } = actions;

// Export the reducer, either as a default or named export
export default reducer;
